import { useNavigate } from "react-router-dom";
import {
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  CreditCard,
  Users,
} from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { selectUser } from "../../store/slices/authSlice";

export default function InsuranceDashboard() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const stats = [
    {
      title: "Total Agents",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Users,
      color: "bg-blue-500",
      onClick: () => navigate("/admin/agents"),
    },
    {
      title: "Total Policies",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Shield,
      color: "bg-emerald-500",
    },
    {
      title: "Pending Claims",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: AlertTriangle,
      color: "bg-orange-500",
    },
    {
      title: "Total Revenue",
      value: "$0",
      change: "0%",
      changeType: "neutral",
      icon: CreditCard,
      color: "bg-purple-500",
    },
  ];

  const quickActions = [
    {
      title: "Manage Agents",
      icon: Users,
      color: "bg-blue-100 text-blue-600",
      available: true,
      onClick: () => navigate("/admin/agents"),
    },
    {
      title: "View Policies",
      icon: Shield,
      color: "bg-gray-100 text-gray-400",
      available: false,
      onClick: () => navigate("/admin/insurance-policies"),
    },
    {
      title: "Review Claims",
      icon: FileText,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
    {
      title: "Analytics",
      icon: BarChart3,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Section */}
      <div className="p-6 text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
        <h1 className="mb-2 text-2xl font-bold">
          Welcome to Insurance Admin, {user?.name || user?.email || "Admin"}! 🛡️
        </h1>
        <p className="text-blue-100">
          Manage insurance policies, claims, and related operations from here.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            onClick={stat.onClick}
            className={`p-6 bg-white rounded-xl border shadow-sm border-gray-200/50 transition-all ${
              stat.onClick
                ? "cursor-pointer hover:shadow-md hover:border-blue-300"
                : ""
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <span
                className={`text-sm font-medium ${
                  stat.changeType === "positive"
                    ? "text-emerald-600"
                    : stat.changeType === "negative"
                    ? "text-red-600"
                    : "text-gray-500"
                }`}
              >
                {stat.change}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {stat.changeType === "neutral" ? "no change" : "from last month"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={!action.available}
              className={`flex flex-col items-center p-4 rounded-lg border transition-colors ${
                action.available
                  ? "border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
                  : "border-gray-100 cursor-not-allowed opacity-60"
              }`}
            >
              <div className={`p-3 rounded-lg ${action.color} mb-3`}>
                <action.icon size={24} />
              </div>
              <span
                className={`text-sm font-medium ${
                  action.available ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {action.title}
                {!action.available && " (Coming Soon)"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Recent Agent Activity</h2>
          <div className="space-y-3">
            <div className="flex justify-center items-center p-8 text-center">
              <div>
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No agent activity yet</p>
                <p className="text-sm text-gray-400">
                  Agents will appear here once they register
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">System Status</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Agent Management</span>
              <span className="text-sm font-medium text-green-600">Online</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Policy Management</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Claims System</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Analytics Dashboard</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

